import { defineStore, storeToRefs } from 'pinia'
import { ApiApplication, ApiApplicationDetails, ApiDependent, CreateDependentModel, EmptyApplicant } from '@/types'
import LSP from '@services/lsp'
import dayjs from 'dayjs'
import { unref } from 'vue'
import { useMessageStore } from '@/stores/message.store'

export type ApiApplicationsOptions = {
  isActive: boolean
}

export const useApplicationStore = defineStore('application', {
  state: () => {
    return {
      applications: undefined as unknown as ApiApplication[],
      error: false,
      isLoading: false,
      message: undefined as unknown as null | string,
      currentApplication: undefined as unknown as ApiApplicationDetails | null,
    }
  },
  actions: {
    async getApplications(filters?: ApiApplicationsOptions) {
      this.error = false
      this.isLoading = false
      this.message = null
      const result = await LSP.all(filters?.isActive)

      if (result.status) {
        this.applications = result.data
      } else {
        this.error = true
        this.message = result.message
      }
      this.isLoading = false
    },
    async updateApplication(applicationId: string, applicant: EmptyApplicant): Promise<void> {
      const messageStore = useMessageStore()
      const { successMessages } = storeToRefs(messageStore)
      this.isLoading = true
      this.error = false
      this.message = null

      const a = unref(applicant)
      const updatedApplication = {
        firstName: a.firstName,
        lastName: a.lastName,
        dateOfBirth: dayjs(a.dateBirth).format('YYYY-MM-DD'),
        sex: a.sex,
        nationality: a.nationality,
        residenceCountry: a.residence,
        residenceDocumentNumber: a.residenceNumber,
        residenceIssueDate: dayjs(a.residenceDate).format('YYYY-MM-DD'),
      }

      const result = await LSP.update(applicationId, updatedApplication)
      if (!result.status) {
        this.error = true
      } else {
        if (this.currentApplication) {
          this.currentApplication.applicant = {
            ...this.currentApplication.applicant,
            ...result.data,
          }
        }
        successMessages.value.push(result.message)
      }
      this.message = result.message
      this.isLoading = false
    },
    async createApplication(application: EmptyApplicant): Promise<string | null> {
      this.isLoading = true
      this.error = false
      this.message = null
      const messageStore = useMessageStore()
      const { successMessages } = storeToRefs(messageStore)

      const a = unref(application)
      const newApplication = {
        firstName: a.firstName,
        lastName: a.lastName,
        dateOfBirth: dayjs(a.dateBirth).format('YYYY-MM-DD'),
        sex: a.sex,
        nationality: a.nationality,
        residenceCountry: a.residence,
        residenceDocumentNumber: a.residenceNumber,
        residenceIssueDate: dayjs(a.residenceDate).format('YYYY-MM-DD'),
      }

      const result = await LSP.create(newApplication)
      if (result.status) {
        this.message = result.message
        successMessages.value.push(result.message)
        return result.data
      } else {
        this.message = result.message
        this.error = true
      }
      this.isLoading = false
      return null
    },
    async getApplication(applicationId: string) {
      this.error = false
      this.message = null
      this.isLoading = true

      const result = await LSP.one(applicationId)

      if (result.status) {
        this.currentApplication = result.data!
      } else {
        this.error = true
      }
      this.isLoading = false
      this.message = result.message!
    },
    async cancelApplication(applicationId: string) {
      this.error = false
      this.message = null
      this.isLoading = true

      const messageStore = useMessageStore()
      const { successMessages } = storeToRefs(messageStore)

      const result = await LSP.cancelApplication(applicationId)

      if (result.status) {
        this.currentApplication = null
        this.applications = []
        successMessages.value.push(result.message)
      } else {
        this.error = true
      }
      this.isLoading = false
      this.message = result.message
    },
    async updateDependent(dependent: ApiDependent) {
      this.error = false
      this.message = null
      this.isLoading = true

      const messageStore = useMessageStore()
      const { successMessages } = storeToRefs(messageStore)

      try {
        const result = await LSP.dependents.update(this.currentApplication?.applicationCode, {
          applicantCode: dependent.applicantCode,
          firstName: dependent.firstName,
          lastName: dependent.lastName,
          relationship: dependent.relationship,
          nationality: dependent.nationality,
          sex: dependent.sex,
          isAccompanying: dependent.isAccompanying == 1,
          dateOfBirth: dayjs(dependent.dateOfBirth).format('YYYY-MM-DD'),
        })
        if (result.status) {
          const idx = this.currentApplication?.dependents.findIndex((a) => a.applicantCode == dependent.applicantCode)
          if (this.currentApplication && idx != null)
            this.currentApplication.dependents[idx] = {
              ...dependent,
              dateOfBirth: dayjs(dependent.dateOfBirth).format('YYYY-MM-DD'),
            }
          successMessages.value.push(result.message)
        }
      } catch (error) {
        this.error = true
        this.message = error.response.data.message ?? error.message
      } finally {
        this.isLoading = false
      }
    },
    async addDependent(dependent: ApiDependent) {
      this.error = false
      this.message = null
      this.isLoading = true

      const messageStore = useMessageStore()
      const { successMessages } = storeToRefs(messageStore)

      try {
        const result = await LSP.dependents.add(this.currentApplication?.applicationCode, {
          firstName: dependent.firstName,
          lastName: dependent.lastName,
          relationship: dependent.relationship,
          nationality: dependent.nationality,
          sex: dependent.sex,
          isAccompanying: dependent.isAccompanying == 1,
          dateOfBirth: dayjs(dependent.dateOfBirth).format('YYYY-MM-DD'),
        } as CreateDependentModel)

        if (result.status) {
          if (this.currentApplication) {
            if (!this.currentApplication.dependents) this.currentApplication.dependents = []
            this.currentApplication.dependents.push({
              applicantCode: result.data.applicantCode,
              applicationReference: this.currentApplication.applicationCode,
              document: null,
              dateOfBirth: result.data.dateOfBirth!,
              firstName: result.data.firstName!,
              isAccompanying: result.data.isAccompanying!,
              lastName: result.data.lastName!,
              nationality: result.data.nationality!,
              relationship: result.data.relationship!,
              sex: result.data.sex!,
            })
          }
          successMessages.value.push(result.message)
        }
      } catch (error) {
        this.error = true
        this.message = error.response.data.message ?? error.message
      } finally {
        this.isLoading = false
      }
    },
    async deleteDependent(applicationId: string, applicantId: string) {
      this.error = false
      this.isLoading = true
      this.message = null
      const messageStore = useMessageStore()
      const { successMessages } = storeToRefs(messageStore)

      const result = await LSP.dependents.remove(applicationId, applicantId)
      if (result.status) {
        if (this.currentApplication && this.currentApplication.dependents) {
          const idx = this.currentApplication.dependents.findIndex(
            (d) => d.applicationReference == applicationId && d.applicantCode == applicantId,
          )
          this.currentApplication.dependents.splice(idx, 1)
          successMessages.value.push(result.message)
        }
      } else {
        this.error = true
      }
      this.isLoading = false
      this.message = result.message
    },
    async removeSupportDocument(applicationId: string, documentTypeCode: string) {
      this.error = false
      this.isLoading = true
      this.message = null
      const messageStore = useMessageStore()
      const { successMessages } = storeToRefs(messageStore)
      const result = await LSP.removeSupportDocument(applicationId, documentTypeCode)
      if (result.status) {
        // remove support document
        const idx = this.currentApplication.applicant.supportDocuments.findIndex(
          (d) => d.documentType == documentTypeCode,
        )

        if (idx != -1) {
          // entry found, update
          this.currentApplication.applicant.supportDocuments.splice(idx, 1)
        }
        successMessages.value.push(result.message)
      } else {
        this.error = true
      }
      this.isLoading = false
      this.message = result.message
    },
    async saveDependentPassportDocument(applicationId: string, applicantId: string, data: any) {
      this.error = false
      this.isLoading = true
      this.message = null
      const messageStore = useMessageStore()
      const { successMessages } = storeToRefs(messageStore)

      // find applicant
      const idx = this.currentApplication?.dependents.findIndex((d) => d.applicantCode == applicantId)
      if (idx != -1) {
        const result = await LSP.dependents.uploadDocument(applicationId, applicantId, data)

        if (result.status) {
          this.currentApplication.dependents[idx].document = { ...result.data }
          successMessages.value.push(result.message)
        } else {
          this.error = true
        }
        this.isLoading = false
        this.message = result.message
      }
    },
    async savePassportDocument(applicationId: string, data: any) {
      this.error = false
      this.isLoading = true
      this.message = null
      const messageStore = useMessageStore()
      const { successMessages } = storeToRefs(messageStore)
      const result = await LSP.uploadDocument(applicationId, data)
      if (result.status) {
        this.currentApplication.applicant.document = { ...result.data }
        successMessages.value.push(result.message)
      } else {
        this.error = true
      }
      this.isLoading = false
      this.message = result.message
    },
    async saveSupportDocument(applicationId: string, data: { type: string; file: File }) {
      this.error = false
      this.isLoading = true
      this.message = null
      const messageStore = useMessageStore()
      const { successMessages } = storeToRefs(messageStore)

      const result = await LSP.uploadSupportDocument(applicationId, {
        type: data.type,
        file: data.file,
      })

      if (result.status) {
        if (this.currentApplication.applicant.supportDocuments == null)
          this.currentApplication.applicant.supportDocuments = []
        // try to locate the document, if found, update the object else insert the object
        const idx = this.currentApplication.applicant.supportDocuments.findIndex(
          (d) => d.documentType == result.data.documentType,
        )

        if (idx != -1) {
          // entry found, update
          this.currentApplication.applicant.supportDocuments[idx] = { ...result.data }
        } else {
          // insert
          this.currentApplication.applicant.supportDocuments.push({ ...result.data })
        }
        successMessages.value.push(result.message)
      } else {
        this.error = true
      }
      this.isLoading = false
      this.message = result.message
    },
    async saveTravelInformation(applicationId: string, travel: any) {
      this.error = false
      this.isLoading = true
      this.message = null
      const messageStore = useMessageStore()
      const { successMessages } = storeToRefs(messageStore)

      const result = await LSP.saveTravelInfo(applicationId, {
        'City / Town / Village': travel.locationctv,
        district: travel.locationDistrict,
        locationCTV: travel.locationctv,
        locationStaying: travel.locationStaying,
        monthStay: travel.monthStay,
        monthsStaying: travel.monthStay,
        travelDateFrom: dayjs(travel.travelDateFrom).format('YYYY-MM-DD'),
      })
      if (result.status) {
        this.currentApplication.travel = {
          ...this.currentApplication?.travel,
          ...result.data,
        }
        successMessages.value.push(result.message)
      } else {
        this.error = true
      }
      this.isLoading = false
      this.message = result.message
    },
    async saveEmploymentInformation(applicationId: string, employment: any) {
      this.error = false
      this.isLoading = true
      this.message = null
      const messageStore = useMessageStore()
      const { successMessages } = storeToRefs(messageStore)

      const result = await LSP.saveEmploymentInfo(applicationId, {
        ...employment,
        salary: parseInt(employment.salary),
        employedSince: dayjs(employment.employedSince).format('YYYY-MM-DD'),
      })
      console.log('res: ', result)
      if (result.status) {
        this.currentApplication.employment = {
          ...this.currentApplication?.employment,
          ...result.data,
        }
        successMessages.value.push(result.message)
      } else {
        this.error = true
      }
      this.isLoading = false
      this.message = result.message
    },
    async getPassportDocument(applicationId: string): Promise<string> {
      this.error = false
      this.isLoading = true
      this.message = null
      const messageStore = useMessageStore()
      const { successMessages } = storeToRefs(messageStore)

      const result = await LSP.getDocument(applicationId)

      if (result.status) {
        successMessages.value.push(result.message ?? 'successfully fetched file')
      } else {
        this.error = true
      }
      this.isLoading = false
      this.message = result.message

      return result.data.document ?? ''
    },
    async getDependentPassportDocument(applicationId: string, dependentId: string): Promise<string> {
      this.error = false
      this.isLoading = true
      this.message = null
      const messageStore = useMessageStore()
      const { successMessages } = storeToRefs(messageStore)

      const result = await LSP.dependents.getDocument(applicationId, dependentId)

      if (result.status) {
        successMessages.value.push(result.message ?? 'successfully fetched file')
      } else {
        this.error = true
      }
      this.isLoading = false
      this.message = result.message

      return result.data.document ?? ''
    },
    async getSupportDocument(applicationId: string, documentTypeCode: string) {
      this.error = false
      this.isLoading = true
      this.message = null
      const messageStore = useMessageStore()
      const { successMessages } = storeToRefs(messageStore)

      const result = await LSP.getSupportDocument(applicationId, documentTypeCode)

      if (result.status) {
        successMessages.value.push(result.message ?? 'successfully fetched file')
      } else {
        this.error = true
      }
      this.isLoading = false
      this.message = result.message

      return result.data.document ?? ''
    },
  },
})
